/* eslint-disable tailwindcss/no-custom-classname */
import useSWR from 'swr'
import { useEffect, useRef, useState } from 'react'
import { initialStyles, Modal } from '@/components/modal/Modal'
import {
  GetVoiceNextResponse,
  GetVoiceSettingResponse,
  VoiceOrder,
  VoiceRepeat,
} from '@/types/voice'
import {
  BGM_LIST,
  RATE_LIST,
  FIRST_BGM_NUM,
  LAST_BGM_NUM,
  getVoiceSettingBgm,
  getVoiceSettingRate,
  setVoiceSettingRate,
  setVoiceSettingBgm,
} from './util'
import {
  NotAllowedAutoPlayModal,
  Time,
  CloseButton,
  SummaryImage,
  SummaryTitle,
  SpeedRateLabel,
  BgmLabel,
} from './'
import {
  AudioPause,
  AudioPlay,
  AudioSkipForward,
  AudioSkipRewind,
} from '@/components/icon'
import { useAudioPlayer } from './useAudioPlayer'
import { mmss } from './util'
import { getNextVoice, postVoiceSummary } from '@/api/voice'
import { postSummaryPercent } from '@/api/summary'
import { useClient } from '@/hooks/useClient'
import { NextVoiceModal } from './NextVoiceModal'
import { useRouter } from 'next/router'

const modalStyle = {
  ...initialStyles,
  content: {
    ...initialStyles.content,
    width: '300px',
  },
}

interface Props {
  isOpen: boolean
  summaryId: number
  image: string
  title: string
  voice: string
  onClose: () => void
}
export const VoiceModal: React.FC<Props> = ({
  isOpen,
  summaryId,
  image,
  title,
  voice,
  onClose,
}) => {
  const isClient = useClient()
  const [open, setOpen] = useState(false)
  const [mouseDown, setMouseDown] = useState(false)
  const [rate, setRate] = useState(null)
  const [bgm, setBgm] = useState(null)

  const voiceRef = useRef<HTMLAudioElement>(null)
  const bgmRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)

  const router = useRouter()

  useEffect(() => {
    setBgm(getVoiceSettingBgm())
    setRate(getVoiceSettingRate())
  }, [])

  const {
    curTime,
    setCurTime,
    duration,
    playing,
    setPlaying,
    setClickedTime,
    setDuration,
    setRate: setAudioRate,
  } = useAudioPlayer(voiceRef)

  // 音声の現在の時間を表示するためのstate。
  // useAudioPlayerのcurTimeと全く同じ値だが、curTimeを表示用に利用すると描画パフォーマンスが悪いため、表示用に別でstateに持つことにした。
  const [curTimeText, setCurTimeText] = useState(0)

  // 再生中curTimeが更新されていくのでcurTimeTextを同期させる
  useEffect(() => {
    setCurTimeText(curTime)
  }, [curTime])

  const { setSrc, setPlaying: setBgmPlaying } = useAudioPlayer(bgmRef)

  const [openNext, setOpenNext] = useState(false)
  const [next, setNext] = useState<GetVoiceNextResponse>(null)
  const { data: setting } =
    useSWR<GetVoiceSettingResponse>('/api/voice/setting')

  const initVoice = () => {
    setCurTime(0)
    setDuration(voiceRef.current.duration)
    setAudioRate(rate)
  }
  const initBgm = () => {
    bgmRef.current.volume = 3 / 7
    bgmRef.current.loop = true
  }

  // 他要約に遷移した時に再生ボタンをリセットする
  useEffect(() => {
    setPlaying(false)
    setBgmPlaying(false)
  }, [router])

  /**
   * 再生開始
   */
  const onClickPlay = () => {
    // 再生履歴を作成
    if (curTime === 0) {
      postVoiceSummary(summaryId)
    }
    setPlaying(true)
    setBgmPlaying(true)
  }

  /**
   * 再生停止
   */
  const onClickPause = () => {
    setPlaying(false)
    setBgmPlaying(false)

    // 読了率を記録
    const percent =
      Math.floor(curTime) === 0
        ? 0
        : (Math.floor(curTime) / Math.floor(duration)) * 100
    postSummaryPercent(summaryId, percent)
  }

  /**
   * 15秒巻き戻し
   */
  const onClickSkipRewind = () => {
    const t = curTime - 15
    const time = t < 0 ? 0.1 : t
    setClickedTime(time)
  }

  /**
   * 15秒早送り
   */
  const onClickSkipForward = () => {
    const t = curTime + 15
    if (t > duration) {
      setClickedTime(duration - 1)
    } else {
      setClickedTime(t)
    }
  }

  /**
   * バーをクリックしたとき
   */
  const onClickProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    setMouseDown(false)
    const currentTime = calc(e)
    // setClickedTime()によりcurTimeを更新しているので処理的にはsetCurTimeText()をする必要はないが、
    // setCurTimeText()をしないとcurTime更新を待たないとバーが動かないため、UX向上のためsetCurTimeText()をしている
    setCurTimeText(currentTime)
    setClickedTime(currentTime)
  }

  /**
   * バーのドラッグしているとき
   */
  const onDragProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mouseDown) return
    const currentTime = calc(e)
    setCurTimeText(currentTime)
    setClickedTime(currentTime)
  }

  /**
   * バーのドラッグを離したとき
   */
  const onDragStop = () => {
    setMouseDown(false)
    setClickedTime(curTimeText)
  }

  /**
   * 再生し終わったとき
   */
  const onEnded = async () => {
    postSummaryPercent(summaryId, 100)
    if (setting?.repeat !== VoiceRepeat.AUTO_PLAY_ON) {
      setPlaying(false)
      setBgmPlaying(false)
      return
    }

    // 1曲リピートの場合は再度流す
    if (setting?.order === VoiceOrder.ORDER_TYP_REPEAT) {
      setClickedTime(0.1)
      return
    }

    // 次の要約を取得しモーダルを表示
    setPlaying(false)
    setBgmPlaying(false)
    const next = await getNextVoice(summaryId)
    if (next.result) {
      onClose()
      setNext(next)
      setOpenNext(true)
    }
  }

  /**
   * さらに次の要約を読むボタンをクリック時
   */
  const onNext = () => {
    setOpenNext(false)
    setTimeout(async () => {
      setOpenNext(true)
      const newNext = await getNextVoice(next?.summary?.id)
      setNext(newNext)
    }, 500)
  }

  const calc = (e: React.MouseEvent<HTMLDivElement>): number => {
    // プログレスバーの位置を取得
    const progressBd = progressRef.current
    const clientRect = progressBd.getBoundingClientRect()
    const positionX = clientRect.left + window.pageXOffset

    // プログレスバー内におけるクリック位置を計算
    const x = e.pageX - positionX
    return (duration / 180) * x
  }

  /**
   * 再生速度をクリック時
   */
  const onClickRate = () => {
    const i = RATE_LIST.findIndex((v) => v === rate)
    const next = i + 1
    const newRate = RATE_LIST.length <= next ? RATE_LIST[0] : RATE_LIST[next]
    setVoiceSettingRate(newRate)
    setRate(newRate)
    setAudioRate(newRate)
  }

  /**
   * BGMをクリック
   */
  const onClickBgm = () => {
    let newBgm
    if (bgm.num === FIRST_BGM_NUM) {
      newBgm = BGM_LIST[1]
    } else if (bgm.num === LAST_BGM_NUM) {
      newBgm = BGM_LIST[0]
    } else {
      const next = bgm.num + 1
      newBgm = BGM_LIST.filter((v) => v.num === next).pop()
    }
    setBgm(newBgm)
    setVoiceSettingBgm(newBgm.num)
    setSrc(`${process.env.IMG_SERVER}/voice/bgm${newBgm?.num}.mp3`)
  }

  return (
    <>
      {/* モーダルを閉じても音声は流れ続けるようにするため、モーダル外に設置 */}
      <audio
        ref={voiceRef}
        onLoadedMetadata={initVoice}
        onEnded={onEnded}
        key={`voice-${summaryId}`}
      >
        {isClient && <source src={voice} />}
        音声再生に対応していないブラウザのため音声を再生できません
      </audio>
      <audio ref={bgmRef} onLoadedMetadata={initBgm} key={`bgm-${summaryId}`}>
        {isClient && (
          <source
            src={`${process.env.IMG_SERVER}/voice/bgm${bgm?.num || 0}.mp3`}
          />
        )}
        音声再生に対応していないブラウザのため音声を再生できません
      </audio>
      {/* 連続再生用モーダル */}
      <Modal
        open={openNext}
        onRequestClose={() => setOpenNext(false)}
        style={modalStyle}
        styleDirect=".ReactModal__Body--open { overflow: auto; }"
      >
        <NextVoiceModal
          next={next}
          onClose={() => setOpenNext(false)}
          onNext={onNext}
        />
      </Modal>
      <Modal
        open={isOpen}
        onRequestClose={onClose}
        style={modalStyle}
        styleDirect=".ReactModal__Body--open { overflow: auto; }"
      >
        <CloseButton onClick={onClose} />
        <div className="relative p-[15px] text-center">
          <SummaryImage src={image} title={title} />
          <SummaryTitle title={title} />
          {/** progress */}
          <div className="flex justify-center">
            <Time text={mmss(curTimeText)} />
            <div
              ref={progressRef}
              className="inline-block relative my-[10px] mx-0 w-[180px] h-[3px] align-middle bg-[#ccc] cursor-pointer select-none"
              onClick={onClickProgress}
            >
              <div
                className="inline-block absolute left-0 h-[3px] bg-[#008cdb] cursor-pointer"
                style={{ width: `${(curTimeText / duration) * 100}%` }}
              >
                <div
                  className="absolute top-[-7px] right-[-9px] w-4 h-4 bg-[#008cdb] rounded-[50%] border-2 border-[#fff] border-solid"
                  onMouseDown={() => setMouseDown(true)}
                  onMouseUp={onDragStop}
                  onMouseOver={() => setMouseDown(false)}
                  onMouseMove={onDragProgress}
                ></div>
              </div>
            </div>
            <Time text={`-${mmss(duration - curTimeText)}`} />
          </div>
          {/** voice-control-button */}
          <div className="flex justify-center items-center">
            <button
              onClick={onClickSkipRewind}
              className="m-4 w-[40px] h-[40px] select-none"
            >
              <AudioSkipRewind />
            </button>
            {playing ? (
              <button
                onClick={onClickPause}
                className="m-4 w-[60px] h-[60px] select-none"
              >
                <AudioPause />
              </button>
            ) : (
              <button
                onClick={onClickPlay}
                className="m-4 w-[60px] h-[60px] select-none"
              >
                <AudioPlay />
              </button>
            )}
            <button
              onClick={onClickSkipForward}
              className="m-4 w-[40px] h-[40px] select-none"
            >
              <AudioSkipForward />
            </button>
          </div>
          {/** voice-control-label */}
          <div className="flex justify-center mx-auto mt-[10px] mb-0">
            <SpeedRateLabel
              onClick={onClickRate}
              text={`${rate?.toFixed(1)}x`}
            />
            <BgmLabel onClick={onClickBgm} text={bgm?.text} />
          </div>
        </div>
      </Modal>
      <NotAllowedAutoPlayModal isOpen={open} onClose={() => setOpen(false)} />
    </>
  )
}
